import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { AuthenticationService } from '@/app/core/services/config/auth.service'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, exhaustMap, map, tap } from 'rxjs/operators'

/** Evita open-redirect: solo rutas relativas que empiezan por un solo `/`. */
function safePostLoginPath(returnUrl: string | null | undefined): string {
  const fallback = '/user/dashboard'
  const raw = returnUrl?.trim()
  if (!raw) return fallback
  if (!raw.startsWith('/') || raw.startsWith('//')) return fallback
  return raw
}

import {
  login,
  loginFailure,
  loginSuccess,
  logout,
  logoutSuccess,
  loadMe,
  loadMeSuccess,
  loadMeFailure,
  totpChallengeRequired,
  verifyTotp,
  verifyTotpSuccess,
  verifyTotpFailure,
  verifyBackupCode,
  verifyBackupCodeSuccess,
  verifyBackupCodeFailure,
} from './auth.actions'
import { clearError } from './auth.actions'

@Injectable()
export class AuthenticationEffects {
  private actions$ = inject(Actions)
  private authService = inject(AuthenticationService)
  private router = inject(Router)

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      exhaustMap(({ username, password, returnUrl }) =>
        this.authService.login(username, password).pipe(
          map((result) =>
            result.kind === 'success'
              ? loginSuccess({ user: result.user, returnUrl })
              : totpChallengeRequired({ returnUrl })
          ),
          catchError((err) =>
            of(
              loginFailure({
                error:
                  err?.error?.message ??
                  err?.message ??
                  'No se pudo iniciar sesión. Intentalo de nuevo.',
              })
            )
          )
        )
      )
    )
  )

  totpChallengeRequired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(totpChallengeRequired),
        tap(({ returnUrl }) => {
          this.router.navigate(['/auth/verify-totp'], {
            queryParams: returnUrl ? { returnUrl } : {},
          })
        })
      ),
    { dispatch: false }
  )

  verifyTotp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyTotp),
      exhaustMap(({ code, returnUrl }) =>
        this.authService.verifyTotp(code).pipe(
          map((user) => verifyTotpSuccess({ user, returnUrl })),
          catchError((err) =>
            of(
              verifyTotpFailure({
                error:
                  err?.error?.message ??
                  err?.message ??
                  'Código inválido o vencido. Probá de nuevo.',
              })
            )
          )
        )
      )
    )
  )

  verifyBackupCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyBackupCode),
      exhaustMap(({ backupCode, returnUrl }) =>
        this.authService.useBackupCode(backupCode).pipe(
          map((user) => verifyBackupCodeSuccess({ user, returnUrl })),
          catchError((err) =>
            of(
              verifyBackupCodeFailure({
                error:
                  err?.error?.message ??
                  err?.message ??
                  'Código de respaldo inválido. Probá de nuevo.',
              })
            )
          )
        )
      )
    )
  )

  loadMe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMe),
      exhaustMap(() =>
        this.authService.getMe().pipe(
          map((me) => loadMeSuccess({ me })),
          catchError((error) => of(loadMeFailure({ error })))
        )
      )
    )
  )

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginSuccess),
      tap(({ returnUrl }) => {
        this.router.navigateByUrl(safePostLoginPath(returnUrl))
      }),
      map(() => loadMe())
    )
  )

  verifyTotpSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyTotpSuccess),
      tap(({ returnUrl }) => {
        this.router.navigateByUrl(safePostLoginPath(returnUrl))
      }),
      map(() => loadMe())
    )
  )

  verifyBackupCodeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyBackupCodeSuccess),
      tap(({ returnUrl }) => {
        this.router.navigateByUrl(safePostLoginPath(returnUrl))
      }),
      map(() => loadMe())
    )
  )

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      exhaustMap(() => {
        this.authService.logout()
        this.router.navigate(['/home'])

        return of(logoutSuccess())
      })
    )
  )
}